import React, { type FC } from "react";
import { useNavigate } from "react-router-dom";
import { useWindows } from "@app/overlay";
import { OPEN_FILTER_QUERY_PARAM } from "@app/router/queryParams";
import { CardHeader } from "@shared/ui/card-header";
import { ListItem } from "@src/components/business-list";
import { BusinessList } from "@src/components/business-list";
import { BusinessBoStateValue, BusinessDivisionValue, BusinessEnterpriseValue } from "@src/components/business-data-display";
import { useBusinessListQuery } from "@src/hooks/useBusinessListQuery";
import { useUrlFilter } from "@shared/hooks/useUrlFilter";
import { useUrlParamTrigger } from "@shared/hooks/useUrlParamTrigger";
import {
    onsideInvoicesServices,
    type OnsideInvoice,
    type OnsideInvoiceExtends,
    type OnsideInvoiceFilter,
} from "../../../../api/onsideInvoices";
import { stockFinishedGoodsShipmentListWindows } from "./stockFinishedGoodsShipmentList.windows";
import "./StockFinishedGoodsShipmentList.scss";

/** Связи списка НС для отгрузки СГП. */
const FINISHED_GOODS_SHIPMENT_LIST_INCLUDES = {
    include: ["WsSender", "Enterprise", "State"] satisfies Array<keyof OnsideInvoiceExtends>,
} as const;

/** Список НС для отгрузки со склада готовой продукции. */
export const StockFinishedGoodsShipmentList: FC = () => {
    const windows = useWindows();
    const navigate = useNavigate();
    const filterState = useUrlFilter<OnsideInvoiceFilter>();

    const list = useBusinessListQuery<OnsideInvoice, OnsideInvoiceFilter>({
        queryKey: "stock.finishedGoodsShipment.invoices",
        filter: filterState.filter,
        queryFn: (filter, signal) =>
            onsideInvoicesServices.load(
                {
                    filter,
                    ...FINISHED_GOODS_SHIPMENT_LIST_INCLUDES,
                },
                { signal },
            ),
        getId: (item) => item.Code,
    });

    const openFilterWindow = (): void => {
        stockFinishedGoodsShipmentListWindows.filter.open(windows, {
            initialFilter: filterState.filter,
            onApply: (filter) => {
                filterState.applyFilter(filter, true);
            },
        });
    };

    useUrlParamTrigger(OPEN_FILTER_QUERY_PARAM, () => {
        openFilterWindow();
    });

    return (
        <div className="stock_finished_goods_shipment_list_page">
            <CardHeader
                text="Список НС для отгрузки со СГП"
                buttons={[
                    {
                        type: "button",
                        text: "Поиск",
                        srcIcon: "/images/common/options.svg",
                        onClick: openFilterWindow,
                    },
                ]}
            />
            <BusinessList list={list} onOpenFilter={openFilterWindow}>
                {list.data.map((item) => (
                    <ListItem
                        key={item.Code}
                        title={`НС №${item.Num}`}
                        props={[
                            { label: "Статус НС", value: <BusinessBoStateValue state={item.State} /> },
                            { label: "Склад", value: <BusinessDivisionValue division={item.WsSender} /> },
                            {
                                label: "Контрагент-получатель",
                                value: <BusinessEnterpriseValue enterprise={item.Enterprise} />,
                            },
                        ]}
                        onClick={() => {
                            navigate({ pathname: `${item.Code}` });
                        }}
                    />
                ))}
            </BusinessList>
        </div>
    );
};
